import {
  Divider,
  Flex,
  HStack,
  Image,
  Text,
  VStack,
  Box,
  Center,
  Badge,
  Grid,
  GridItem,
  Icon,
  BoxProps,
} from '@chakra-ui/react';
import { TicketResponse } from 'api/responseTypes';
import { ReactComponent as PlaceholderTeamLogo } from 'assets/placeholder.svg';
import {
  CalendarIcon,
  ClockIcon,
  DoorOpenIcon,
  UserIcon,
  PhoneIcon,
  Grid2X2Icon,
  TicketIcon,
} from 'lucide-react';
import { FormattedMessage } from 'react-intl';

interface TicketInfoProps {
  item: TicketResponse;
  gate: string;
  holderPhone: string;
  holderName: string;
  sector: string;
  isMatch: boolean;
  seatNumber: string;
  row: string;
  section: string;
  ticketType?: string;
  category: {
    key: string;
    label: string;
    color: string;
  };
}

export const TicketInfo = ({
  item,
  gate,
  holderPhone,
  holderName,
  isMatch,
  seatNumber,
  row,
  section,
  ticketType,
  category,
}: TicketInfoProps) => {
  const commonBoxStyles: BoxProps = {
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s',
    _hover: { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' },
  };

  const gradientBoxStyles: BoxProps = {
    bg: 'linear-gradient(135deg, #E8F5F3 0%, #D4EDE8 100%)',
    border: '1px solid',
    borderColor: '#B8DDD5',
    ...commonBoxStyles,
  };

  const seatInfoBoxStyles: BoxProps = {
    bg: 'white',
    px: '16px',
    py: '10px',
    borderRadius: '12px',
    minW: '60px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  };

  const InfoCard = ({
    icon,
    label,
    value,
    colSpan = 1,
    useGradient = false,
  }: {
    icon: React.ElementType;
    label: string | React.ReactElement;
    value: string;
    colSpan?: number;
    useGradient?: boolean;
  }) => (
    <GridItem colSpan={colSpan}>
      <Box {...(useGradient ? gradientBoxStyles : { bg: '#F8F9FA', ...commonBoxStyles })} p='16px'>
        <VStack spacing='8px' align={useGradient ? 'center' : 'start'}>
          <HStack spacing='8px' color='#326755'>
            <Icon as={icon} boxSize='20px' />
            <Text
              fontSize='sm'
              fontWeight={useGradient ? 'semibold' : 'medium'}
              color={useGradient ? '#326755' : '#666'}
            >
              {label}
            </Text>
          </HStack>
          <Text
            fontSize='md'
            fontWeight='bold'
            color='#282E2E'
            textAlign={useGradient ? 'center' : 'left'}
          >
            {value}
          </Text>
        </VStack>
      </Box>
    </GridItem>
  );

  const SeatInfo = ({ label, value }: { label: string; value: string }) => (
    <VStack spacing='8px' flex='1'>
      <Text fontSize='sm' fontWeight='medium' color='#666'>
        {label}
      </Text>
      <Box {...seatInfoBoxStyles}>
        <Text fontSize='xl' fontWeight='bold' color='#FF5C4F' textAlign='center'>
          {value}
        </Text>
      </Box>
    </VStack>
  );
  const renderEventDetails = () => {
    switch (isMatch) {
      case true:
        if (!item.match) return null;
        return (
          <Box
            bg='#F8F9FA'
            borderRadius='16px'
            p={{ base: '20px', md: '24px' }}
            boxShadow='0 2px 8px rgba(0, 0, 0, 0.05)'
          >
            <HStack justify='space-between' spacing='16px' flexDirection='row-reverse'>
              <VStack spacing='12px' flex='1'>
                <Box
                  bg='white'
                  p='12px'
                  borderRadius='50%'
                  boxShadow='0 4px 12px rgba(0, 0, 0, 0.1)'
                >
                  <Image
                    src={item.match.teamA.logo}
                    alt={`${item.match.teamA.name} logo`}
                    boxSize='48px'
                    borderRadius='50%'
                    objectFit='contain'
                    fallback={<PlaceholderTeamLogo />}
                  />
                </Box>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight='bold'
                  color='#282E2E'
                  textAlign='center'
                  noOfLines={2}
                >
                  {item.match.teamA.name}
                </Text>
              </VStack>
              <Center height='80px'>
                <Text fontSize='2xl' fontWeight='bold' color='#326755' px='8px'>
                  VS
                </Text>
              </Center>
              <VStack spacing='12px' flex='1'>
                <Box
                  bg='white'
                  p='12px'
                  borderRadius='50%'
                  boxShadow='0 4px 12px rgba(0, 0, 0, 0.1)'
                >
                  <Image
                    src={item.match.teamB.logo}
                    alt={`${item.match.teamB.name} logo`}
                    boxSize='48px'
                    borderRadius='50%'
                    objectFit='contain'
                    fallback={<PlaceholderTeamLogo />}
                  />
                </Box>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight='bold'
                  color='#282E2E'
                  textAlign='center'
                  noOfLines={2}
                >
                  {item.match.teamB.name}
                </Text>
              </VStack>
            </HStack>
          </Box>
        );

      case false:
        if (!item.concert) return null;
        return (
          <Box
            bg='#F8F9FA'
            borderRadius='16px'
            p={{ base: '20px', md: '24px' }}
            boxShadow='0 2px 8px rgba(0, 0, 0, 0.05)'
          >
            <HStack spacing='16px' justify='space-between' flexDirection='row-reverse'>
              <VStack align='end' spacing='12px' flex='1'>
                <Text fontSize='lg' fontWeight='bold' color='#282E2E' textAlign='right'>
                  {item.concert.title}
                </Text>
                <Text fontSize='md' color='#666' textAlign='right'>
                  {item.concert.city}
                </Text>
              </VStack>
              <Box
                flexShrink={0}
                boxShadow='0 4px 12px rgba(0, 0, 0, 0.1)'
                borderRadius='12px'
                overflow='hidden'
              >
                <Image
                  src={item.concert.imageLink}
                  alt={item.concert.title}
                  boxSize='100px'
                  objectFit='cover'
                />
              </Box>
            </HStack>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <VStack
      align='stretch'
      p={{ base: '20px', md: '24px' }}
      spacing='20px'
      aria-label='ticket information'
    >
      <HStack justifyContent='center' alignItems='center' gap='12px' py='8px'>
        <Badge
          color='white'
          bg={category.color || 'linear-gradient(135deg, #326755 0%, #2a5647 100%)'}
          fontSize='16px'
          fontWeight='bold'
          px='16px'
          py='8px'
          borderRadius='12px'
          boxShadow='0 2px 8px rgba(50, 103, 85, 0.3)'
        >
          {gate}
        </Badge>
        <Text fontSize='lg' fontWeight='bold' color='#282E2E'>
          : البوابة
        </Text>
      </HStack>
      <Box
        bgGradient='linear-gradient(135deg, #FFF5F3 0%, #FFEBE8 100%)'
        borderRadius='16px'
        p={{ base: '20px', md: '24px' }}
        boxShadow='0 2px 12px rgba(255, 92, 79, 0.1)'
        border='2px solid'
        borderColor='#FFD4CF'
      >
        <Text
          color='#FF5C4F'
          fontSize='xl'
          fontWeight='bold'
          textAlign='center'
          mb='20px'
          letterSpacing='wide'
        >
          معلومات المقعد
        </Text>
        <Flex
          justify='space-around'
          align='center'
          flexDirection='row-reverse'
          gap={{ base: '16px', md: '24px' }}
        >
          {seatNumber && <SeatInfo label='المقعد' value={seatNumber} />}
          <SeatInfo label='الصف' value={row} />
          <SeatInfo label='القطاع' value={section} />
        </Flex>
      </Box>
      <Divider borderColor='#E0E0E0' opacity='0.6' />
      {renderEventDetails()}
      <Divider borderColor='#E0E0E0' opacity='0.6' />
      <Grid templateColumns='repeat(2, 1fr)' gap='16px' dir='rtl'>
        <InfoCard
          icon={CalendarIcon}
          label='تاريخ الحدث'
          value={isMatch ? item.match.date ?? '' : item.concert.date ?? ''}
        />
        <InfoCard
          icon={ClockIcon}
          label='وقت البداية'
          value={isMatch ? item.match.time : item.concert.time}
        />
        {item.match && (
          <InfoCard
            icon={DoorOpenIcon}
            label='فتح البوابة'
            value='تفتح البوابات قبل 3 ساعات من بدأ الحدث'
            colSpan={2}
            useGradient={true}
          />
        )}
        {item.concert && (
          <InfoCard
            icon={DoorOpenIcon}
            label='فتح البوابة'
            value='تفتح البوابات قبل ساعة من بدأ الحدث'
            colSpan={2}
            useGradient={true}
          />
        )}
        <InfoCard
          icon={Grid2X2Icon}
          label='الفئة'
          value={category.label}
          colSpan={2}
          useGradient={true}
        />
        {ticketType && (
          <InfoCard
            icon={TicketIcon}
            label='نوع التذكرة'
            value={ticketType}
            colSpan={2}
            useGradient={true}
          />
        )}
      </Grid>
      <Divider borderColor='#E0E0E0' opacity='0.6' />
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap='16px'>
        <InfoCard
          icon={PhoneIcon}
          label={
            <FormattedMessage id='ticketGenerator.phoneNumber' defaultMessage='Phone number' />
          }
          value={holderPhone}
          useGradient={true}
        />
        <InfoCard
          icon={UserIcon}
          label={
            <FormattedMessage id='ticketGenerator.ticketHolder' defaultMessage='Ticket Holder' />
          }
          value={holderName}
          useGradient={true}
        />
      </Grid>
    </VStack>
  );
};
